import { type ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { AuthSplash } from '../components/AuthSplash'
import { NoOrgRoleScreen } from '../components/NoOrgRoleScreen'
import { useCan } from './useCan'
import type { Permission } from './permissions'

interface OrgRoleGateProps {
  /** Permiso del mapa rol→permisos que abre la página. */
  permission: Permission
  children: ReactNode
  /**
   * Qué ve el usuario SIN organización (token sin organizationId). Por
   * defecto nada; las rutas del portal pasan el onboarding de primer negocio
   * (el mismo `firstTime` de BusinessesPage).
   */
  noOrgFallback?: ReactNode
  /**
   * La ruta deja pasar al usuario SIN organización (wizard del alta primera
   * vez — no romper el autoservicio). Ignora `noOrgFallback`.
   */
  allowWithoutOrg?: boolean
  /**
   * Qué ve el usuario CON organización pero sin el permiso. Por defecto la
   * pantalla "pide un rol al dueño" (en las rutas del goal 1 los tres roles
   * comparten la lectura, así que sin permiso ≈ sin rol). Las rutas
   * OWNER-only (Equipo — goal portal-roles-2) pasan aquí su 403 propio: un
   * Gestor SÍ tiene rol y el mensaje de "pide un rol" lo confundiría.
   */
  noPermissionFallback?: ReactNode
}

/**
 * Gate de ruta del portal con el fallback de TRES estados (goal
 * portal-roles-1, espejo de la matriz que el gateway aplica server-side):
 *
 * 1. Sin organización → onboarding de primer negocio (`noOrgFallback`), o la
 *    propia página si `allowWithoutOrg` (rutas del alta).
 * 2. Con organización y SIN rol de organización → pantalla "pide un rol al
 *    dueño" (el backend responde FORBIDDEN a sus queries; nunca se cierra
 *    sesión por autorización — raíz §5.6).
 * 3. Con rol (permiso del mapa) → la página.
 */
export function OrgRoleGate({
  permission,
  children,
  noOrgFallback = null,
  allowWithoutOrg = false,
  noPermissionFallback,
}: OrgRoleGateProps) {
  const { user, loading } = useAuth()
  const allowed = useCan(permission)

  if (loading) return <AuthSplash message="Verificando tu acceso…" />
  if (!user) return null

  // Backend devuelve `organization` como objeto con campos en `null` cuando
  // aún no hay organización; por eso se chequea `id` (patrón DashboardPage).
  const hasOrganization = !!user.organization?.id
  if (!hasOrganization) {
    return allowWithoutOrg ? <>{children}</> : <>{noOrgFallback}</>
  }

  if (!allowed) return <>{noPermissionFallback ?? <NoOrgRoleScreen />}</>

  return <>{children}</>
}
